import { TournamentConditionTypeEnum, TournamentDetailsData } from 'config/types/tournament'
import { Trans } from 'react-i18next'
import styled from 'styled-components'
import Box from 'UIKit/Box/Box'
import EmptyContent from 'UIKit/EmptyContent'
import Table, { TableBody } from 'UIKit/Table/Table'
import TableHeader from 'UIKit/Table/TableHeader'
import { TableCellHistory, TableRowHistory } from 'UIKit/Table/TableHistory'
import LoadingTable from 'UIKit/Table/TableHistory/LoadingTable'
import Text from 'UIKit/Text'
import { range } from 'utils'
import { renderCompactLeaderBoardHeader } from 'views/Tournament/config'
import { useActiveRankRange, useCompactTable } from '../../hooks'
import CompactRow from './CompactRow'

type LeaderBoardTableProps = {
  tournamentDetails: TournamentDetailsData
  retrievalTime?: number
}

const CompactTable: React.FC<LeaderBoardTableProps> = ({ tournamentDetails, retrievalTime }) => {
  const { rows, userInfo, aboveUserInfo, isLoading } = useCompactTable(tournamentDetails, retrievalTime)
  const tableHeader = renderCompactLeaderBoardHeader(tournamentDetails?.conditionType)
  const activeRankRange = useActiveRankRange(rows)

  const isHideUserWithMultiplierType =
    tournamentDetails?.conditionType === TournamentConditionTypeEnum.Multiplier && Number(userInfo?.multiplier) === 0

  const isDisplayUserInfo =
    Number(userInfo?.wagerAmount) > 0 &&
    !isHideUserWithMultiplierType &&
    !rows.find((item) => item.rank === userInfo.rank || item.userCode === userInfo.userCode)

  const isDisplayAboveUserInfo =
    isDisplayUserInfo &&
    !isHideUserWithMultiplierType &&
    (aboveUserInfo?.rank < activeRankRange?.firstRank || aboveUserInfo?.rank > activeRankRange?.lastRank) &&
    tournamentDetails?.rewards[aboveUserInfo?.rank]

  return (
    <Table>
      <thead>
        <tr>
          {tableHeader.map((item) => {
            return (
              <StyledTableHeader
                $isLastCol={item.id === 2}
                key={item.name}
                width={[item.width]}
                padding={['8px', '8px', '12px']}
              >
                <Text
                  letterSpacing="-0.28px"
                  textAlign="left"
                  fontSize="14px"
                  lineHeight="20px"
                  color="textAlt1"
                  whiteSpace="nowrap"
                  fontWeight={700}
                >
                  <Trans>{item.label}</Trans>
                </Text>
              </StyledTableHeader>
            )
          })}
        </tr>
      </thead>

      {isLoading ? (
        <>
          <TableRowHistory>
            <TableCellHistory
              style={{
                position: 'relative',
              }}
              className="loader"
              colSpan={100}
              pl="0px !important"
            >
              <LoadingTable loading={isLoading} top="0" />
            </TableCellHistory>
          </TableRowHistory>

          {range(0, 3).map((idx) => (
            <TableRowHistory key={`table-row-loader-${idx}`}>
              <TableCellHistory className="loader" colSpan={100} pl="0px !important">
                <Box width="100%" height="52px" />
              </TableCellHistory>
            </TableRowHistory>
          ))}
        </>
      ) : rows?.length === 0 ? (
        <StyledEmptyContent>
          <TableCellHistory colSpan={tableHeader.length} style={{ textAlign: 'center' }}>
            <EmptyContent content="No player has participated yet" justifyContent="center" py="20px" width="100%" />
          </TableCellHistory>
        </StyledEmptyContent>
      ) : (
        <TableBody>
          {rows.map((item) => {
            return (
              <CompactRow
                conditionType={tournamentDetails.conditionType}
                isActiveUser={item.userCode && userInfo?.userCode === item.userCode}
                key={item.userCode}
                {...item}
              />
            )
          })}

          {isDisplayAboveUserInfo && (
            <CompactRow conditionType={tournamentDetails.conditionType} isAboveActiveUser {...aboveUserInfo} />
          )}

          {isDisplayUserInfo && (
            <CompactRow conditionType={tournamentDetails.conditionType} isActiveUser {...userInfo} />
          )}
        </TableBody>
      )}
    </Table>
  )
}

const StyledTableHeader = styled(TableHeader)<{ $isLastCol }>`
  background: transparent;
  ${({ $isLastCol }) =>
    $isLastCol &&
    `
      display: flex;
      justify-content: flex-end;
      padding: 8px 0px;
      `}
`

const StyledEmptyContent = styled(TableRowHistory)`
  *:hover {
    background: rgb(30, 37, 45);
    cursor: not-allowed;
  }
`
export default CompactTable
